import axiosInstance from './axios-instance';
import axios from 'axios';

export const getPresignedUrls = async (filenames) => {
  try {
    const response = await axiosInstance.post('/api/v1/backend/bug-reports/presigned-urls', {
      filenames
    });
    return response;
  } catch (error) {
    console.error('Error getting pre-signed URLs:', error);
    throw error;
  }
};

// Import the getContentType function like the publisher service uses
const getContentType = (fileName) => {
  const extension = fileName.split('.').pop()?.toLowerCase();
  if (!extension) return 'application/octet-stream';

  const TYPE_IMAGES = ['jpeg', 'jpg', 'png', 'gif', 'svg', 'webp'];
  const TYPE_VIDEOS = ['mp4', 'webm', 'avi'];
  const TYPE_AUDIOS = ['mp3', 'm4a', 'aac', 'wav', 'ogg'];

  if (TYPE_IMAGES.includes(extension)) {
    // Convert webp to jpeg, but keep svg as svg+xml
    if (extension === 'jpg' || extension === 'webp') {
      return 'image/jpeg';
    } else if (extension === 'svg') {
      return 'image/svg+xml';
    } else {
      return 'image/' + extension;
    }
  }

  if (TYPE_VIDEOS.includes(extension)) {
    return 'video/' + extension;
  }

  if (TYPE_AUDIOS.includes(extension)) {
    switch (extension) {
      case 'mp3': return 'audio/mpeg';
      case 'm4a': return 'audio/mp4';
      case 'aac': return 'audio/aac';
      case 'wav': return 'audio/wav';
      case 'ogg': return 'audio/ogg';
    }
  }
  
  return 'application/octet-stream';
};

export const uploadFileToS3 = async (file, presignedUrl) => {
  try {
    console.log('Uploading file:', {
      name: file.name,
      type: file.type,
      size: file.size,
      contentType: getContentType(file.name.toLowerCase()),
      presignedUrl: presignedUrl
    });
    
    // Convert File to ArrayBuffer for binary upload (like the working publisher service)
    const arrayBuffer = await file.arrayBuffer();
    
    // Use fetch with ALL the required headers that match the signature
    const response = await fetch(presignedUrl, {
      method: 'PUT',
      body: arrayBuffer, // Send binary data, not File object
      headers: {
        'Content-Type': getContentType(file.name.toLowerCase()),
        'content-disposition': 'inline',
        'x-amz-acl': 'public-read'
      },
      mode: 'cors',
      credentials: 'omit' // Explicitly don't send credentials
    });
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Upload failed (${response.status}): ${errorText}`);
    }
    
    console.log('Upload successful:', response.status);
    return response;
  } catch (error) {
    console.error('Error uploading file to S3:', error);
    console.error('Error details:', error);
    throw error;
  }
};

export const submitBugReport = async (formData) => {
  try {
    let screenshotUrls = [];
    
    // If there are screenshots, upload them first
    if (formData.screenshots && formData.screenshots.length > 0) {
      try {
        // Get pre-signed URLs for all screenshots
        const filenames = formData.screenshots.map(file => file.name);
        console.log('Requesting presigned URLs for:', filenames);
        const presignedResponse = await getPresignedUrls(filenames);
        console.log('Got presigned response:', presignedResponse);
        
        // Upload each screenshot to S3
        const uploadPromises = formData.screenshots.map(async (file, index) => {
          const presignedData = presignedResponse.data[index];
          console.log('Uploading file to:', presignedData.url);
          await uploadFileToS3(file, presignedData.url);
          
          // Generate the public URL for the uploaded file
          const baseUrl = process.env.REACT_APP_S3_BASE_URL || presignedData.url.split('?')[0];
          return baseUrl.includes(presignedData.key) ? baseUrl : `${baseUrl}/${presignedData.key}`;
        });
        
        screenshotUrls = await Promise.all(uploadPromises);
        console.log('All screenshots uploaded successfully:', screenshotUrls);
      } catch (uploadError) {
        console.error('Screenshot upload failed, submitting report without screenshots:', uploadError);
        // Continue without screenshots if upload fails
        screenshotUrls = [];
      }
    }
    
    // Prepare the final data for submission
    const submitData = {
      type: formData.type,
      title: formData.title,
      description: formData.description,
      priority: formData.priority,
      email: formData.email,
      isAnonymous: formData.isAnonymous,
      url: formData.url,
      userAgent: formData.userAgent,
      metadata: formData.metadata,
      screenshots: screenshotUrls
    };

    // Submit the bug report with screenshot URLs
    const response = await axiosInstance.post('/api/v1/backend/bug-reports', submitData);
    return response;
  } catch (error) {
    console.error('Error submitting bug report:', error);
    throw error;
  }
};

export const getBugReports = async (params = {}) => {
  try {
    const response = await axiosInstance.get('/api/v1/backend/bug-reports', { params });
    return response;
  } catch (error) {
    console.error('Error fetching bug reports:', error);
    throw error;
  }
};

export const updateBugReportStatus = async (id, status) => {
  try {
    const response = await axiosInstance.patch(`/api/v1/backend/bug-reports/${id}/status`, { status });
    return response;
  } catch (error) {
    console.error('Error updating bug report status:', error);
    throw error;
  }
};
